import React, { useCallback, useEffect, useState } from "react";

import { getAllPosts, getCourses, getSearchSuggestions } from "services";

import { recordPageView, trackMixpanel, useCancelAsyncOnUnmount, useLocation, useNavigate, useParams, useTranslation, useUserContext } from "utils";

import { CoachMark, Col, ErrorBoundary, FilterSet, IllustrationCard, MainPage, Row, Svg, Tabs } from "components";

import { ArticleResults } from "pages/app/articles-news/components/SearchResults";
import { CourseResults } from "pages/app/courses/components/SearchResults";
import { SearchInput } from "pages/app/search/components/SearchInput";

const Search = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const signal = useCancelAsyncOnUnmount()();
  const { type, term, link } = useParams();

  const { t } = useTranslation(["search", "meta"]);

  const { user } = useUserContext();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [noMore, setNoMore] = useState(false);
  const [articles, setArticles] = useState([false, false]);
  const [courses, setCourses] = useState([false, false]);
  const [searching, setSearching] = useState(false);
  const [currentType, setCurrentType] = useState(false);
  const [filter, setFilter] = useState([]);
  const [page, setPage] = useState(0);
  const [timer, setTimer] = useState({});
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [share] = useState({
    title: term ? `${t("meta:search.title")} · ${term.replace(/-/g, " ")}` : t("meta:search.title"),
    summary: term ? `${term.replace(/-/g, " ")} · ${t("meta:search.description")}` : t("meta:search.description"),
    link: `${process.env.REACT_APP_FARM_URL}/search`,
    image: `${process.env.REACT_APP_FARM_URL}/images/misc/share.png`,
  });
  const [currentLink, setCurrentLink] = useState(undefined);

  const loadSuggestions = () => {
    getSearchSuggestions({
      signal,
    }).then((res) => {
      setSuggestions(res.length <= 0 ? [] : res);
    });
  };

  const loadArticles = useCallback(() => {
    getAllPosts({
      page: page,
      search: search,
      filter: filter,
      signal,
    })
      .then((res) => {
        if (!res) {
          return;
        }

        setArticles(page > 0 ? [...articles, ...res] : res);
        setCourses([]);
        if (res.length < 6) {
          setNoMore(true);
        }
      })
      .finally(() => {
        setLoadingMore(false);
        setSearching(false);
        setLoading(false);
      });
  }, [articles, filter, page, search, signal]);

  const loadCourses = useCallback(() => {
    getCourses({
      search: search,
      page: page,
      signal,
    })
      .then((res) => {
        setArticles([]);
        setCourses(page > 0 ? [...courses, ...res] : res);
        if (res.length < 6) {
          setNoMore(true);
        }
      })
      .finally(() => {
        setLoadingMore(false);
        setSearching(false);
        setLoading(false);
      });
  }, [courses, page, search, signal]);

  const getStuff = useCallback(() => {
    if (currentType === "news" || currentType === "articles") {
      loadArticles();
    } else if (currentType === "courses") {
      loadCourses();
    }
    // eslint-disable-next-line
  }, [currentType, loadArticles, loadCourses, search]);

  const loadMore = () => {
    if (!noMore && !loadingMore) {
      setLoadingMore(true);
      setPage(page + 1);
      setLoading(false);
    }
  };

  const searchPosts = (s) => {
    // clear route searching
    if ((!s || s === "") && term && type) {
      navigate(user ? `/search?filter=${currentType}` : `/search?filter=${currentType}`);
    } else {
      navigate({
        search: `?filter=${currentType}&search=${s}`,
      });
    }

    trackMixpanel("Search", { search: s, type: currentType });

    setSearch(s);
    setPage(0);
    setNoMore(false);
    setLoadingMore(true);
    setSearching(true);
    setArticles([false, false]);
  };

  const searchStuff = (s) => {
    setTimer(clearTimeout(timer));
    if (s.length >= 3 || s.length === 0) {
      setTimer(
        setTimeout(() => {
          searchPosts(s);
        }, 1000)
      );
    }
  };

  const toggleFilter = (f) => {
    if (filter !== f) {
      setFilter(f);
      setPage(0);
      setNoMore(false);
      setLoadingMore(true);
      setSearching(true);
      setArticles([false, false]);

      const url = new URL(window.location.href);

      url.searchParams.delete("starred");
      history.pushState({}, "", url); // eslint-disable-line no-restricted-globals
    }
  };

  const filterType = (t) => {
    if (currentType !== t) {
      setFilter([]);
      setCurrentType(t);
      setPage(0);
      setNoMore(false);
      setLoadingMore(true);
      setSearching(true);
      setArticles([false, false]);

      navigate({
        search: `?filter=${t}&search=${search}`,
      });
    }
  };

  const tabs = [
    {
      label: t("tabs.articles"),
      key: "articles",
    },
  ];

  const userTabs = [...tabs, { label: t("tabs.courses"), key: "courses" }];

  useEffect(() => {
    getStuff();
  }, [currentType, filter, page, search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("starred") === "true") {
      setFilter(["starred"]);
    }

    if (currentLink !== link) {
      setLoading(true);
      setCurrentLink(link);
    } else if (params.get("filter")) {
      setCurrentType(params.get("filter") && params.get("filter") !== "false" ? params.get("filter") : "articles");
    }
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    recordPageView("search", "search");
    loadSuggestions();

    if (term) {
      setCurrentType(type && type !== "false" ? type.replace("news", "articles") : "articles");
      setSearch(term.replace(/-/g, " "));
    } else {
      const params = new URLSearchParams(location.search);
      setSearch(params.get("search") && params.get("search") !== "false" ? params.get("search") : "");
      setCurrentType(params.get("filter") && params.get("filter") !== "false" ? params.get("filter") : "articles");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filterSettings = () => {
    const filters = {
      starred: {
        icon: "tabler/bookmark",
        title: t("filters.starred"),
      },
    };

    if (currentType === "articles") {
      filters.recommended = {
        title: t("filters.recommended"),
      };
    }

    return {
      selectedFilters: [...filter],
      onClearFilters: () => toggleFilter([]),
      onSelectFilter: (key) => toggleFilter([...filter, key]),
      onUnselectFilter: (index) => toggleFilter(index === 0 ? [] : [filter[0]]),
      filters,
    };
  };

  return (
    <MainPage loading={loading} pageName={"search"} share={share}>
      <section>
        <Row className="padding-bottom-0">
          <Col>
            {currentType && <Tabs defaultActiveKey={currentType} onChange={(v) => filterType(v)} activeKey={currentType} items={user ? userTabs : tabs} />}
            <CoachMark location="ant-tabs-nav-list" type="search" />
          </Col>
        </Row>
        <Row>
          <Col>
            <SearchInput
              type={currentType}
              initial={term && term !== "false" ? term.replace(/-/g, " ") : search}
              search={(v) => searchStuff(v)}
              suggestions={suggestions}
            />
          </Col>
        </Row>
        {user && <FilterSet {...filterSettings()} />}
      </section>
      <section className="padded-top">
        <Row className="margin-bottom-200">
          <Col>{search ? <h3>{t("headline.results", { search: search })}</h3> : <h3>{t(`headline.${currentType}`)}</h3>}</Col>
        </Row>
        {(articles && articles.length > 0) || (courses && courses.length > 0) ? (
          <>
            {currentType === "articles" && (
              <ErrorBoundary>
                <ArticleResults
                  articles={articles}
                  loading={loading}
                  loadingMore={loadingMore}
                  loadMore={loadMore}
                  noMore={noMore}
                  search={search}
                  searching={searching}
                  user={user}
                />
              </ErrorBoundary>
            )}
            {currentType === "courses" && (
              <ErrorBoundary>
                <CourseResults
                  courses={courses}
                  noMore={noMore}
                  loadingMore={loadingMore}
                  searching={searching}
                  loading={loading}
                  loadMore={loadMore}
                  search={search}
                />
              </ErrorBoundary>
            )}
          </>
        ) : (
          <>
            <Row>
              <Col lg={14}>
                <IllustrationCard>
                  <Svg svgName="misc/no_search_results" />
                </IllustrationCard>
              </Col>
            </Row>
            <Row>
              <Col>
                <p style={{ maxWidth: "768px" }}>{t("noResults")}</p>
              </Col>
            </Row>
          </>
        )}
      </section>
    </MainPage>
  );
};

export default Search;
